/**
 * Point type and operations for the MD3 Expressive Shape Engine.
 * Ported from androidx.graphics.shapes.Point.kt
 *
 * In Kotlin, Point is a type alias for FloatFloatPair. Here we use a plain object
 * for zero-overhead structural typing in TypeScript.
 */

import {
	DISTANCE_EPSILON,
	directionVector,
	distance,
	interpolate,
	type PointTransformer,
} from "../utils/utils";

/** A 2D point or vector. */
export interface Point {
	readonly x: number;
	readonly y: number;
}

/**
 * Creates a new Point.
 * @param x - X coordinate
 * @param y - Y coordinate
 */
export function point(x: number, y: number): Point {
	return { x, y };
}

/**
 * Returns the magnitude (distance from origin) of the point.
 * @param p - The point
 */
export function getDistance(p: Point): number {
	return distance(p.x, p.y);
}

/**
 * Returns the squared magnitude of the point (cheaper than getDistance).
 * @param p - The point
 */
export function getDistanceSquared(p: Point): number {
	return p.x * p.x + p.y * p.y;
}

/**
 * Dot product of two points (treated as vectors).
 * @param a - First point
 * @param b - Second point
 */
export function dotProduct(a: Point, b: Point): number {
	return a.x * b.x + a.y * b.y;
}

/**
 * Rotates the point 90 degrees counter-clockwise.
 * @param p - The point
 * @returns New rotated point
 */
export function rotate90(p: Point): Point {
	return { x: -p.y, y: p.x };
}

/**
 * Returns the unit vector in the direction of the point from (0,0).
 * @param p - The point
 * @throws Error if the point is at the origin
 */
export function getDirection(p: Point): Point {
	const d = getDistance(p);
	if (d < DISTANCE_EPSILON)
		throw new Error("Can't get direction of zero-length vector");
	return { x: p.x / d, y: p.y / d };
}

/**
 * Checks if p0→p1→p2 turn is clockwise (positive cross product in screen coords).
 * @param p0 - Previous point
 * @param p1 - Current point
 * @param p2 - Next point
 */
export function clockwise(p0: Point, p1: Point, p2: Point): boolean {
	const v1x = p1.x - p0.x;
	const v1y = p1.y - p0.y;
	const v2x = p2.x - p1.x;
	const v2y = p2.y - p1.y;
	return v1x * v2y - v1y * v2x > 0;
}

/** Point addition. */
export function addPoints(a: Point, b: Point): Point {
	return { x: a.x + b.x, y: a.y + b.y };
}

/** Point subtraction. */
export function subtractPoints(a: Point, b: Point): Point {
	return { x: a.x - b.x, y: a.y - b.y };
}

/** Scalar multiplication. */
export function scalePoint(p: Point, scalar: number): Point {
	return { x: p.x * scalar, y: p.y * scalar };
}

/** Scalar division. */
export function dividePoint(p: Point, scalar: number): Point {
	return { x: p.x / scalar, y: p.y / scalar };
}

/**
 * Linearly interpolates between two points.
 * @param start - Start point (at fraction=0)
 * @param stop - End point (at fraction=1)
 * @param fraction - Interpolation factor [0,1]
 */
export function lerpPoint(start: Point, stop: Point, fraction: number): Point {
	return {
		x: interpolate(start.x, stop.x, fraction),
		y: interpolate(start.y, stop.y, fraction),
	};
}

/**
 * Returns a direction unit vector from dx/dy components.
 * @param dx - X component
 * @param dy - Y component
 */
export function pointDirection(dx: number, dy: number): Point {
	const [nx, ny] = directionVector(dx, dy);
	return { x: nx, y: ny };
}

/**
 * Applies a PointTransformer to a point.
 * @param p - The original point
 * @param fn - The transform function
 */
export function transformPoint(p: Point, fn: PointTransformer): Point {
	const [tx, ty] = fn(p.x, p.y);
	return { x: tx, y: ty };
}
